import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const { loginWithEmail, registerWithEmail } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [deviceName, setDeviceName] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("ff_device_name") || "";
  });
  const [busy, setBusy] = useState(false);
  const [deviceLimitInfo, setDeviceLimitInfo] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await loginWithEmail(email, password, deviceName);
      else await registerWithEmail(email, password, name, deviceName);
      nav("/app");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Hard device cap — show a friendly modal instead of a toast.
      if (detail && typeof detail === "object" && detail.code === "device_limit_reached") {
        setDeviceLimitInfo(detail);
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || "Authentication failed");
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF9F6]">
      {/* Visual side */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1540420773420-3366772f4999?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80"
          alt="Fresh salad"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#2A402D]/70 via-[#3B5A3E]/30 to-transparent" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-2 text-lg font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
            <img
              src="/logo.png"
              alt="Shop, Cal & Meal"
              className="w-9 h-9 rounded-full object-cover ring-1 ring-white/40"
              data-testid="brand-logo"
            />
            Shop, Cal & Meal
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl sm:text-5xl font-semibold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Paste a recipe.<br />Get the basket.
            </h1>
            <p className="mt-4 text-white/85 text-base">
              The healthy-eating sidekick for UK shoppers. Save recipes, count calories, and
              send your ingredients straight to Tesco, Sainsbury&apos;s, Asda, Morrisons, Waitrose, Aldi, Lidl or Co-op.
            </p>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md ff-fade-in">
          <div className="lg:hidden flex items-center gap-2 text-lg font-medium mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>
            <img
              src="/logo.png"
              alt="Shop, Cal & Meal"
              className="w-9 h-9 rounded-full object-cover"
              data-testid="brand-logo-mobile"
            />
            Shop, Cal & Meal
          </div>
          <div className="ff-label mb-3">{mode === "login" ? "Welcome back" : "Get started"}</div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {mode === "login" ? "Sign in to Shop, Cal & Meal" : "Create your account"}
          </h2>
          <p className="text-[#5C6B61] mt-2 mb-8">
            {mode === "login" ? "Save recipes & shop smarter." : "It takes about ten seconds."}
          </p>

          <button
            type="button"
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-3 border border-[#E5E2DA] hover:border-[#3B5A3E] bg-white py-3 rounded-full transition"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
              <path fill="#EA4335" d="M12 11v3.2h5.2c-.25 1.4-1.7 4-5.2 4-3.13 0-5.7-2.6-5.7-5.7s2.57-5.7 5.7-5.7c1.78 0 2.97.76 3.65 1.4l2.5-2.4C16.6 3.7 14.5 2.8 12 2.8 6.9 2.8 2.8 6.9 2.8 12s4.1 9.2 9.2 9.2c5.3 0 8.8-3.7 8.8-9 0-.6-.07-1.06-.16-1.5H12z"/>
            </svg>
            <span className="font-medium">Continue with Google</span>
          </button>

          {mode === "login" && (
            <button
              type="button"
              data-testid="create-account-cta"
              onClick={() => setMode("register")}
              className="w-full mt-3 flex items-center justify-center gap-2 bg-[#3B5A3E] hover:bg-[#2A4230] text-white py-3 rounded-full font-semibold transition shadow-sm hover:shadow-md"
            >
              <span aria-hidden>✨</span>
              <span>Create an account</span>
            </button>
          )}

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-[#E5E2DA]" />
            <span className="ff-label">or with email</span>
            <div className="h-px flex-1 bg-[#E5E2DA]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="ff-label block mb-2">Name</label>
                <input
                  className="ff-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jamie"
                  data-testid="register-name-input"
                />
              </div>
            )}
            <div>
              <label className="ff-label block mb-2">Email</label>
              <input
                type="email"
                className="ff-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="ff-label block mb-2">Password</label>
              <input
                type="password"
                className="ff-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="login-password-input"
              />
            </div>
            <div>
              <label className="ff-label block mb-2">
                Name this device {mode === "register"
                  ? <span className="text-[#A33D26] normal-case tracking-normal font-normal">(required — used for support &amp; security)</span>
                  : <span className="text-[#8B9690] normal-case tracking-normal font-normal">(optional, helps if you ever need to sign one out)</span>}
              </label>
              <input
                type="text"
                className="ff-input"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="e.g. My iPhone, Work Laptop"
                maxLength={60}
                required={mode === "register"}
                minLength={mode === "register" ? 1 : 0}
                data-testid="login-device-name-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-btn"
              className="w-full ff-btn-primary disabled:opacity-60"
            >
              {busy ? "Just a moment…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          {mode === "register" && (
            <p className="text-sm text-[#5C6B61] mt-6 text-center">
              Already have one?{" "}
              <button onClick={() => setMode("login")} className="text-[#3B5A3E] font-medium underline-offset-4 hover:underline">
                Sign in
              </button>
            </p>
          )}

          <Link
            to="/extension"
            data-testid="login-extension-pill"
            className="mt-6 flex items-center justify-center gap-2 text-xs font-medium text-[#3B5A3E] hover:text-[#2A402D] transition"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#3B5A3E] animate-pulse" />
            New · Send your basket to your supermarket — faster checkout →
          </Link>
        </div>
      </div>
      {deviceLimitInfo && (
        <DeviceLimitModal info={deviceLimitInfo} onClose={() => setDeviceLimitInfo(null)} />
      )}
    </div>
  );
}

function DeviceLimitModal({ info, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-[#2A332C]/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="device-limit-modal"
    >
      <div
        className="ff-card w-full max-w-md p-6 max-h-[85vh] max-h-[85dvh] overflow-y-auto overscroll-contain relative my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ff-label">Device limit reached</div>
        <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          You&apos;re already signed in to {info.limit} devices
        </h2>
        <p className="text-sm text-[#5C6B61] mt-2 leading-relaxed">
          Shop, Cal &amp; Meal allows {info.limit} active devices per account. To sign in here, please sign out of one of these first — open the app on that device and go to <strong>Profile → Devices → Sign out</strong>.
        </p>
        <div className="mt-4 space-y-2">
          {(info.devices || []).map((d, i) => (
            <div key={i} className="rounded-xl border border-[#E5E2DA] bg-[#FAF6EE] p-3 flex items-start gap-3" data-testid={`limit-device-${i}`}>
              <span className="text-2xl flex-none mt-0.5">
                {/(iphone|ipad|android)/i.test(d.device_label || "") ? "📱" : "💻"}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#2A332C]">{d.device_name || d.device_label || "Unknown device"}</div>
                <div className="text-xs text-[#5C6B61] mt-0.5">
                  {d.device_name && d.device_label ? <>{d.device_label} · </> : null}
                  {d.geo_label || "Unknown location"}
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#8B9690] mt-4 leading-relaxed">
          Don&apos;t recognise one of these? Someone else may be using your account. Reset your password and sign out everywhere from your Profile.
        </p>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="ff-btn-primary" data-testid="device-limit-ok">Got it</button>
        </div>
      </div>
    </div>
  );
}
